import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { siteConfig } from '@/data/siteData';
import { 
  Cloud, 
  Shield, 
  Monitor, 
  Code,
  ChevronDown,
  Menu,
  X,
  Phone,
  Mail,
  ArrowRight,
  Headphones,
  Facebook,
  Linkedin
} from 'lucide-react';

const services = [
  {
    category: 'Compute',
    icon: Monitor,
    href: '/services/compute',
    items: [
      { name: 'Servers', href: '/services/compute/servers' },
      { name: 'Laptops & Desktops', href: '/services/compute/laptops-desktops' },
      { name: 'Storage', href: '/services/compute/storage' },
      { name: 'Hyper Convergence', href: '/services/compute/hyper-convergence' },
      { name: 'Cloud', href: '/services/compute/cloud' },
      { name: 'Co-Location', href: '/services/compute/colocation' },
      { name: 'Backup', href: '/services/compute/backup' },
    ]
  },
  {
    category: 'Network & Security',
    icon: Shield,
    href: '/services/network',
    items: [
      { name: 'Switches & Routers', href: '/services/network/switches-routers' },
      { name: 'Access Points', href: '/services/network/wireless' },
      { name: 'Next-Gen Firewall', href: '/services/network/ngfw' },
      { name: 'Email Security', href: '/services/network/email-security' },
      { name: 'EDR / XDR / NDR', href: '/services/network/edr-xdr' },
      { name: 'SIEM / SOAR / UEBA', href: '/services/network/siem-soar' },
      { name: 'Data Leakage Prevention', href: '/services/network/dlp' },
      { name: 'SASE / SD-WAN', href: '/services/network/sase-sdwan' },
      { name: 'Multi-Factor Auth (MFA)', href: '/services/network/mfa' },
      { name: 'Privileged Access (PAM)', href: '/services/network/pam' },
      { name: 'Encryption & Key Mgmt', href: '/services/network/encryption' },
      { name: 'NAC / Micro-Segmentation', href: '/services/network/nac' },
      { name: 'Firewall Management', href: '/services/network/firewall-mgmt' },
      { name: 'DRM / EDRM', href: '/services/network/drm' },
      { name: 'CASB', href: '/services/network/casb' },
      { name: 'Mobile Security (UEM)', href: '/services/network/uem' },
      { name: 'WAF / API Security', href: '/services/network/waf' },
      { name: 'VAPT', href: '/services/network/vapt' },
      { name: 'Vulnerability Management', href: '/services/network/vulnerability-mgmt' },
    ]
  },
  {
    category: 'Monitoring',
    icon: Cloud,
    href: '/services/monitoring',
    items: [
      { name: 'Network Monitoring', href: '/services/monitoring/network-monitoring' },
      { name: 'Server & Storage Monitoring', href: '/services/monitoring/server-monitoring' },
      { name: 'Database Monitoring', href: '/services/monitoring/database-monitoring' },
      { name: 'Application Monitoring', href: '/services/monitoring/application-monitoring' },
      { name: 'SOC Monitoring', href: '/services/monitoring/soc-monitoring' },
      { name: 'Wireless Monitoring', href: '/services/monitoring/wireless-monitoring' },
    ]
  },
  {
    category: 'Services',
    icon: Code,
    href: '/services/services',
    items: [
      { name: 'VAPT', href: '/services/services/vapt' },
      { name: 'Compliance Audit', href: '/services/services/compliance-audit' },
      { name: 'Security Awareness Training', href: '/services/services/security-training' },
      { name: 'ISO 27001 Certification', href: '/services/services/iso-training' },
      { name: 'GRC Services', href: '/services/services/grc' },
      { name: 'Cloud Services', href: '/services/services/cloud-services' },
      { name: 'Hardware & Network AMC', href: '/services/services/amc' },
      { name: 'Facility Management (FMS)', href: '/services/services/fms' },
    ]
  },
];

const navLinks = [
  { name: 'Home', href: '/' },
  { name: 'About Us', href: '/about' },
  { name: 'Services', href: '/services', hasMega: true },
  { name: 'Clients', href: '/clients' },
  { name: 'Alliances', href: '/alliances' },
  { name: 'Case Studies', href: '/case-studies' },
  { name: 'Contact Us', href: '/contact' },
];

export const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMegaOpen, setIsMegaOpen] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [activeService, setActiveService] = useState(0);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
      if (window.scrollY > 100 && isMegaOpen) {
        setIsMegaOpen(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isMegaOpen]);

  useEffect(() => {
    setIsMobileOpen(false);
    setIsMegaOpen(false);
  }, [location.pathname]);

  return (
    <>
      {/* Top Bar - Dark background, white icons */}
      <div className="bg-[#1a1a1a] py-2 hidden lg:block">
        <div className="container mx-auto px-6 flex justify-between items-center">
          <div className="flex items-center gap-6 text-sm">
            <a href={`tel:${siteConfig.phone}`} className="flex items-center gap-2 text-white hover:text-[#0066CC] transition-colors">
              <Phone className="w-4 h-4 text-white" />
              <span className="text-white/70">Call Us:</span> 
              <span className="text-white">{siteConfig.phone}</span>
            </a>
            <span className="text-white/30">|</span>
            <a href={`mailto:${siteConfig.email}`} className="flex items-center gap-2 text-white hover:text-[#0066CC] transition-colors">
              <Mail className="w-4 h-4 text-white" />
              <span className="text-white">{siteConfig.email}</span>
            </a>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-white/70">Connect With Us</span>
            <div className="flex gap-2">
              <a href="#" className="w-7 h-7 rounded-full bg-white/20 flex items-center justify-center hover:bg-[#0066CC] transition-colors">
                <Facebook className="w-3.5 h-3.5 text-white" />
              </a>
              <a href="#" className="w-7 h-7 rounded-full bg-white/20 flex items-center justify-center hover:bg-[#0066CC] transition-colors">
                <Linkedin className="w-3.5 h-3.5 text-white" />
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Main Navbar */}
      <motion.header
        className={`sticky top-0 z-50 transition-all duration-300 ${
          isScrolled
            ? 'bg-white/95 backdrop-blur-lg shadow-md'
            : 'bg-white'
        }`}
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="container mx-auto px-4 lg:px-6">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center group">
              <img 
                src="https://customer-assets.emergentagent.com/job_felix-apple/artifacts/46cryd4k_Untitled%20design%20%2845%29.png" 
                alt="Felix Infotech" 
                className="h-12 w-auto"
              />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-1">
              {navLinks.map((link) => (
                <div
                  key={link.name}
                  className="relative"
                  onMouseEnter={() => link.hasMega && setIsMegaOpen(true)}
                  onMouseLeave={() => link.hasMega && setIsMegaOpen(false)}
                >
                  {link.hasMega ? (
                    <span
                      className={`px-4 py-2 text-sm font-medium flex items-center gap-1 transition-colors cursor-pointer ${
                        location.pathname.startsWith('/services') ? 'text-[#0066CC]' : 'text-gray-700 hover:text-[#0066CC]'
                      }`}
                    >
                      {link.name}
                      <ChevronDown
                        className={`w-4 h-4 transition-transform duration-200 ${
                          isMegaOpen ? 'rotate-180' : ''
                        }`}
                      />
                    </span>
                  ) : (
                    <Link
                      to={link.href}
                      className={`px-4 py-2 text-sm font-medium flex items-center gap-1 transition-colors ${
                        location.pathname === link.href ? 'text-[#0066CC]' : 'text-gray-700 hover:text-[#0066CC]'
                      }`}
                    >
                      {link.name}
                    </Link>
                  )}
                </div>
              ))}
            </nav>

            {/* CTA Button - Light bg, blue icon */}
            <div className="hidden lg:flex items-center gap-4">
              <Link to="/contact">
                <Button className="bg-[#0066CC] hover:bg-[#0055AA] text-white font-semibold shadow-lg">
                  <Headphones className="w-4 h-4 mr-2 text-white" />
                  Get Support
                  <ArrowRight className="w-4 h-4 ml-2 text-white" />
                </Button>
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
              onClick={() => setIsMobileOpen(!isMobileOpen)}
            >
              {isMobileOpen ? (
                <X className="w-6 h-6 text-gray-700" />
              ) : (
                <Menu className="w-6 h-6 text-gray-700" />
              )}
            </button>
          </div>
        </div>

        {/* Mega Menu */}
        <AnimatePresence>
          {isMegaOpen && (
            <motion.div
              className="absolute top-full left-0 right-0 bg-white border-t border-gray-200 shadow-xl hidden lg:block"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
              onMouseEnter={() => setIsMegaOpen(true)}
              onMouseLeave={() => setIsMegaOpen(false)}
            >
              <div className="container mx-auto px-6 py-8">
                <div className="grid grid-cols-12 gap-8">
                  {/* Service Categories - Light bg, blue icons */}
                  <div className="col-span-3 space-y-2">
                    <p className="text-xs uppercase tracking-wider text-gray-500 mb-4 font-semibold">
                      Our Services
                    </p>
                    {services.map((service, index) => (
                      <button
                        key={service.category}
                        className={`w-full text-left px-4 py-3 rounded-lg flex items-center gap-3 transition-all ${
                          activeService === index
                            ? 'bg-[#0066CC] text-white shadow-lg'
                            : 'hover:bg-gray-100 text-gray-700'
                        }`}
                        onMouseEnter={() => setActiveService(index)}
                      >
                        <service.icon className={`w-5 h-5 ${activeService === index ? 'text-white' : 'text-[#0066CC]'}`} />
                        <span className="font-medium">{service.category}</span>
                      </button>
                    ))}
                  </div>

                  {/* Service Items */}
                  <div className="col-span-5 border-l border-gray-200 pl-8">
                    <motion.div
                      key={activeService}
                      initial={{ opacity: 0, x: 10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      <div className="flex items-center gap-3 mb-6">
                        {(() => {
                          const IconComponent = services[activeService].icon;
                          return (
                            <div className="w-12 h-12 rounded-xl bg-[#0066CC] flex items-center justify-center">
                              <IconComponent className="w-6 h-6 text-white" />
                            </div>
                          );
                        })()}
                        <div>
                          <h3 className="font-semibold text-lg text-gray-900">
                            {services[activeService].category}
                          </h3>
                          <p className="text-sm text-gray-500">
                            Explore our {services[activeService].category.toLowerCase()} offerings
                          </p>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        {services[activeService].items.map((item) => (
                          <Link
                            key={item.name}
                            to={item.href}
                            className="px-4 py-3 rounded-lg hover:bg-gray-100 flex items-center gap-2 group transition-colors"
                            onClick={() => setIsMegaOpen(false)}
                          >
                            <ArrowRight className="w-4 h-4 text-[#0066CC] opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                            <span className="text-sm font-medium text-gray-700">{item.name}</span>
                          </Link>
                        ))}
                      </div>
                      <div className="mt-6">
                        <Link 
                          to={services[activeService].href}
                          className="text-[#0066CC] text-sm font-medium flex items-center gap-2 hover:underline"
                          onClick={() => setIsMegaOpen(false)}
                        >
                          View all {services[activeService].category} <ArrowRight className="w-4 h-4" />
                        </Link>
                      </div>
                    </motion.div>
                  </div>

                  {/* CTA Section - Dark bg (blue), white icons */}
                  <div className="col-span-4 bg-[#0066CC] rounded-2xl p-6">
                    <h4 style={{ color: 'white' }} className="font-semibold text-lg mb-2">
                      Need Expert Consultation?
                    </h4>
                    <p style={{ color: 'rgba(255,255,255,0.8)' }} className="text-sm mb-4">
                      Our team of experts is ready to help you find the perfect solution for your business needs.
                    </p>
                    <div className="flex flex-col gap-3">
                      <a
                        href={`tel:${siteConfig.phone}`}
                        className="flex items-center gap-2 text-sm hover:opacity-80 transition-opacity"
                      >
                        <Phone className="w-4 h-4 text-white" />
                        <span style={{ color: 'white' }}>{siteConfig.phone}</span>
                      </a>
                      <a
                        href={`mailto:${siteConfig.email}`}
                        className="flex items-center gap-2 text-sm hover:opacity-80 transition-opacity"
                      >
                        <Mail className="w-4 h-4 text-white" />
                        <span style={{ color: 'white' }}>{siteConfig.email}</span>
                      </a>
                    </div>
                    <Link to="/contact" onClick={() => setIsMegaOpen(false)}>
                      <Button className="mt-4 bg-white text-[#0066CC] hover:bg-gray-100 w-full font-semibold">
                        Get Free Quote
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMobileOpen && (
            <motion.div
              className="lg:hidden absolute top-full left-0 right-0 bg-white border-t border-gray-200 shadow-xl max-h-[calc(100vh-5rem)] overflow-y-auto"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
            >
              <div className="p-4 space-y-4">
                {navLinks.map((link) => (
                  <div key={link.name}>
                    {link.hasMega ? (
                      <div className="space-y-2">
                        <p className="font-semibold text-gray-900 px-4 py-2">{link.name}</p>
                        {services.map((service) => (
                          <div key={service.category} className="pl-4">
                            <p className="text-sm font-medium text-[#0066CC] px-4 py-2 flex items-center gap-2">
                              <service.icon className="w-4 h-4 text-[#0066CC]" />
                              {service.category}
                            </p>
                            <div className="pl-6 space-y-1">
                              {service.items.slice(0, 3).map((item) => (
                                <Link
                                  key={item.name}
                                  to={item.href}
                                  className="block text-sm text-gray-600 px-4 py-1.5 hover:text-[#0066CC] transition-colors"
                                  onClick={() => setIsMobileOpen(false)}
                                >
                                  {item.name}
                                </Link>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <Link
                        to={link.href}
                        className="block px-4 py-3 font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                        onClick={() => setIsMobileOpen(false)}
                      >
                        {link.name}
                      </Link>
                    )}
                  </div>
                ))}
                <div className="pt-4 border-t border-gray-200">
                  <Link to="/contact" onClick={() => setIsMobileOpen(false)}>
                    <Button className="w-full bg-[#0066CC] text-white">
                      <Headphones className="w-4 h-4 mr-2 text-white" />
                      Get Support
                    </Button>
                  </Link>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.header>
    </>
  );
};

export default Navbar;
